import {
  Code,
  Database,
  LineChart,
  Globe,
  Brain,
  Layers,
  Settings,
  Cpu,
  Laptop,
  ChartBar,
  BarChartBig,
  Webhook,
  PenTool,
  Component,
  Briefcase,
  Network,
  Sparkles,
  ServerCog,
} from "lucide-react";

const courses = [
  // 🌟 Featured / High-Demand Courses
  {
    title: "Full Stack Development",
    slug: "full-stack-development",
    icon: <Layers />,
    category: "Web Development",
    level: "Beginner–Advanced",
    rating: 4.8,
    students: 2100,
    label: "Featured",
    showOnHome: true,
    description:
      "Become a full-stack developer with expertise in frontend, backend, API development, and deployment.",
  },

  {
    title: "Python",
    slug: "python-programming",
    icon: <Brain />,
    category: "Programming",
    level: "Beginner–Advanced",
    rating: 4.9,
    students: 1830,
    label: "Featured",
    showOnHome: true,
    description:
      "Learn Python programming, script automation, data processing, and backend application development.",
  },

  {
    title: "Java",
    slug: "java-programming",
    icon: <Code />,
    category: "Programming",
    level: "Beginner–Advanced",
    rating: 4.7,
    students: 1500,
    label: "New",
    description:
      "Build scalable backend applications using Core Java, OOP, JDBC, and Spring Boot.",
  },

  {
    title: "C / C++ Programming",
    slug: "c-cpp-programming",
    icon: <Code />,
    category: "Programming",
    level: "Beginner",
    rating: 4.5,
    students: 900,
    label: "New",
    description:
      "Strengthen programming fundamentals, algorithms, memory management, and system-level development.",
  },

  {
    title: "Networking & Infrastructure Management",
    slug: "networking-infrastructure-management",
    icon: <Network />,
    category: "IT Infrastructure",
    level: "Beginner",
    rating: 4.7,
    students: 980,
    label: "Popular",
    description:
      "Learn network setup, configuration, routing, security, and enterprise IT infrastructure management.",
  },

  // 💼 Career-Oriented & Professional Skills
  {
    title: "HR Training & Office Automation",
    slug: "hr-training-office-automation",
    icon: <Briefcase />,
    category: "Professional Skills",
    level: "Beginner",
    rating: 4.6,
    students: 600,
    label: "Popular",
    showOnHome: true,
    description:
      "Training in HR processes, onboarding, payroll basics, documentation, and MS Office productivity.",
  },

  {
    title: "Power BI",
    slug: "power-bi",
    icon: <BarChartBig />,
    category: "Data Analytics",
    level: "Beginner",
    rating: 4.8,
    students: 1100,
    label: "Popular",
    showOnHome: true,
    description:
      "Learn professional business dashboards, data visualization, DAX, and real-world BI reporting.",
  },

  {
    title: "Data Analytics",
    slug: "data-analytics",
    icon: <ChartBar />,
    category: "Data Analytics",
    level: "Beginner",
    rating: 4.7,
    students: 1300,
    label: "Featured",
    showOnHome: true,
    description:
      "Gain skills in Excel, SQL, BI tools, and data interpretation for analytical job roles.",
  },

  {
    title: "Data Science",
    slug: "data-science",
    icon: <Brain />,
    category: "Data Science",
    level: "Intermediate",
    rating: 4.8,
    students: 980,
    label: "Featured",
    description:
      "Learn machine learning fundamentals, statistical models, predictive analytics, and ML workflows.",
  },

  {
    title: "SQL",
    slug: "sql",
    icon: <Database />,
    category: "Data Science",
    level: "Beginner",
    rating: 4.7,
    students: 1400,
    label: "New",
    description:
      "Master database querying, joins, relational design, and SQL for professional applications.",
  },

  // 🌐 Web & Marketing Courses
  {
    title: "HTML, CSS, JS, React",
    slug: "frontend-development",
    icon: <Globe />,
    category: "Web Development",
    level: "Beginner",
    rating: 4.9,
    students: 1900,
    label: "Popular",
    description:
      "Learn frontend development, modern UI creation, responsive layouts, and real-world React components.",
  },

  {
    title: "PHP, WordPress",
    slug: "php-wordpress",
    icon: <Webhook />,
    category: "Web Development",
    level: "Beginner",
    rating: 4.6,
    students: 1000,
    label: "Popular",
    description:
      "Build dynamic web applications and professional WordPress sites with custom themes & plugins.",
  },

  {
    title: "Digital Marketing",
    slug: "digital-marketing",
    icon: <LineChart />,
    category: "Marketing",
    level: "Beginner",
    rating: 4.8,
    students: 2200,
    label: "Popular",
    showOnHome: true,
    description:
      "Learn SEO, SEM, social media marketing, Google ads, analytics, and brand growth strategies.",
  },

  // 🤖 Advanced & Specialized Courses
  {
    title: "AI & ML",
    slug: "ai-machine-learning",
    icon: <Brain />,
    category: "AI/ML",
    level: "Intermediate–Advanced",
    rating: 4.9,
    students: 1220,
    label: "Featured",
    showOnHome: true,
    description:
      "Deep dive into machine learning algorithms, neural networks, AI models, and applied ML techniques.",
  },

  {
    title: "Testing & Automation",
    slug: "testing-automation",
    icon: <Settings />,
    category: "QA/Automation",
    level: "Beginner",
    rating: 4.6,
    students: 850,
    label: "New",
    description:
      "Learn manual testing, automated testing workflows, Selenium, QA processes, and test writing.",
  },

  {
    title: "Embedded Systems & VLSI",
    slug: "embedded-systems-vlsi",
    icon: <Cpu />,
    category: "Electronics",
    level: "Intermediate",
    rating: 4.7,
    students: 680,
    label: "Popular",
    description:
      "Study microcontrollers, silicon logic, embedded programming, and chip-level engineering.",
  },

  {
    title: "AutoCAD",
    slug: "autocad",
    icon: <PenTool />,
    category: "Design",
    level: "Beginner",
    rating: 4.7,
    students: 720,
    label: "Popular",
    description:
      "Learn professional CAD drafting, engineering drawing, and architectural blueprint design.",
  },

  // 🧾 ERP & Business Solutions
  {
    title: "SAP",
    slug: "sap",
    icon: <Component />,
    category: "ERP",
    level: "Beginner–Advanced",
    rating: 4.8,
    students: 970,
    label: "Popular",
    description:
      "Understand SAP ERP modules used for enterprise-level business process management.",
  },

  {
    title: "Microsoft Dynamics",
    slug: "microsoft-dynamics",
    icon: <Laptop />,
    category: "ERP",
    level: "Beginner–Advanced",
    rating: 4.7,
    students: 850,
    label: "Popular",
    description:
      "Train in Microsoft Dynamics CRM, business automation, and workflow optimization.",
  },

  {
    title: "Generative AI",
    slug: "generative-ai",
    icon: <Sparkles />,
    category: "AI/ML",
    level: "Beginner–Advanced",
    rating: 4.9,
    students: 1200,
    label: "Featured",
    showOnHome: true,
    description:
      "Learn generative AI concepts, prompt engineering, AI tools, and practical workflows for real-world applications.",
  },

  {
    title: "AI-Powered Digital Marketing",
    slug: "ai-powered-digital-marketing",
    icon: <LineChart />,
    category: "Marketing",
    level: "Beginner–Advanced",
    rating: 4.9,
    students: 950,
    label: "New",
    showOnHome: true,
    description:
      "Learn how AI can support SEO, content creation, campaign planning, analytics, and digital marketing workflows.",
  },

  {
    title: "DevOps",
    slug: "devops",
    icon: <ServerCog />,
    category: "IT Infrastructure",
    level: "Beginner–Advanced",
    rating: 4.9,
    students: 850,
    label: "New",
    showOnHome: true,
    description:
      "Learn DevOps fundamentals, CI/CD, version control, automation, containers, and modern deployment workflows.",
  },

  {
    title: "Placement Training",
    slug: "placement-training",
    icon: <Briefcase />,
    category: "Professional Skills",
    level: "Beginner–Advanced",
    rating: 4.9,
    students: 800,
    label: "Popular",
    showOnHome: true,
    description:
      "Build interview readiness through aptitude practice, communication skills, resume preparation, mock interviews, and career guidance.",
  },
];

export default courses;